import { type FormEvent, useEffect, useState } from "react";
import type { Maintenance } from "@statuspage/shared";
import {
  adminCreateMaintenance,
  adminDeleteMaintenance,
  fetchAdminMaintenance,
  type MaintenanceCreatePayload,
} from "../../lib/api";

const INITIAL_FORM: MaintenanceCreatePayload = {
  title: "",
  description: null,
  scheduled_at: "",
  duration_min: 60,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isMaintenance(value: unknown): value is Maintenance {
  if (!isRecord(value)) return false;

  return (
    typeof value.id === "string" &&
    typeof value.tenant_id === "string" &&
    (value.service_id === null || typeof value.service_id === "string") &&
    typeof value.title === "string" &&
    (value.description === null || typeof value.description === "string") &&
    typeof value.scheduled_at === "string" &&
    typeof value.duration_min === "number" &&
    typeof value.created_at === "string"
  );
}

function isMaintenanceArray(value: unknown): value is Maintenance[] {
  return Array.isArray(value) && value.every(isMaintenance);
}

export function MaintenancePage(): React.ReactNode {
  const [items, setItems] = useState<Maintenance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<MaintenanceCreatePayload>(INITIAL_FORM);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAdminMaintenance();
      if (!isMaintenanceArray(result.maintenance)) {
        throw new Error("Ungültiges Wartungsdatenformat");
      }
      setItems(result.maintenance);
    } catch {
      setError("Wartungen konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await adminCreateMaintenance(form);
      setForm(INITIAL_FORM);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Wartung konnte nicht gespeichert werden");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Wartung wirklich löschen?")) return;
    try {
      await adminDeleteMaintenance(id);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen");
    }
  };

  if (loading) {
    return <div className="px-6 py-8 dark:bg-slate-950 dark:text-slate-100">Lade Wartungen…</div>;
  }

  return (
    <main className="mx-auto max-w-4xl px-6 py-8 dark:bg-slate-950 dark:text-slate-100">
      <h1 className="mb-4 text-2xl font-bold dark:text-slate-100">Wartungsfenster</h1>
      {error && <p className="mb-4 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-200">{error}</p>}

      <form className="mb-6 grid gap-3 rounded border border-slate-200 bg-white p-4 sm:grid-cols-2 dark:border-slate-800 dark:bg-slate-900" onSubmit={handleSubmit}>
        <label className="text-sm" htmlFor="maintenance-title">
          Titel
          <input
            id="maintenance-title"
            className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={form.title}
            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
            required
          />
        </label>

        <label className="text-sm" htmlFor="maintenance-scheduled-at">
          Geplantes Datum (ISO)
          <input
            id="maintenance-scheduled-at"
            className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={form.scheduled_at}
            onChange={(event) => setForm((current) => ({ ...current, scheduled_at: event.target.value }))}
            required
            placeholder="2026-05-02T10:00:00Z"
          />
        </label>

        <label className="text-sm sm:col-span-2" htmlFor="maintenance-description">
          Beschreibung
          <input
            id="maintenance-description"
            className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={form.description ?? ""}
            onChange={(event) =>
              setForm((current) => ({ ...current, description: event.target.value.trim() === "" ? null : event.target.value }))
            }
          />
        </label>

        <label className="text-sm" htmlFor="maintenance-duration-min">
          Dauer in Minuten
          <input
            id="maintenance-duration-min"
            type="number"
            min={1}
            max={1440}
            className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            value={form.duration_min}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                duration_min: Number(event.target.value),
              }))
            }
            required
          />
        </label>

        <div className="flex items-end">
          <button type="submit" className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400">
            Wartung planen
          </button>
        </div>
      </form>

      <section className="space-y-3">
        {items.map((item) => (
          <article key={item.id} className="rounded border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h2 className="font-semibold text-slate-900 dark:text-slate-100">{item.title}</h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {item.scheduled_at} · {item.duration_min} Min.
                </p>
                {item.description ? <p className="text-sm text-slate-700 dark:text-slate-300">{item.description}</p> : null}
              </div>
              <button type="button" className="text-sm text-red-600 hover:underline dark:text-red-400" onClick={() => handleDelete(item.id)}>
                Löschen
              </button>
            </div>
          </article>
        ))}
      </section>
    </main>
  );
}
